import { MONTH_NAMES_PT } from '@practium/shared';

const PAYMENT_METHOD_LABEL: Record<string, string> = {
	credit_card: 'Cartão',
	card: 'Cartão',
	pix: 'PIX',
	boleto: 'Boleto',
	free: 'Cortesia',
};

const PAYMENT_METHOD_SUFFIX: Record<string, string> = {
	credit_card: 'no cartão',
	card: 'no cartão',
	pix: 'no PIX',
	boleto: 'no boleto',
	free: 'sem cobrança',
};

export const formatCents = (cents?: number | null) =>
	((cents ?? 0) / 100).toLocaleString('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	});

export const formatPerThousand = (priceCents: number, tokens: number) => {
	if (!tokens) return '—';
	const perMil = priceCents / 100 / (tokens / 1000);
	return `${perMil.toLocaleString('pt-BR', {
		style: 'currency',
		currency: 'BRL',
		minimumFractionDigits: 3,
		maximumFractionDigits: 3,
	})} por mil`;
};

const toDate = (value?: string | Date | null) => {
	if (!value) return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value?: string | Date | null) => {
	const date = toDate(value);
	if (!date) return '—';
	return date.toLocaleDateString('pt-BR');
};

export const formatDayMonth = (value?: string | Date | null) => {
	const date = toDate(value);
	if (!date) return '—';
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	return `${day}/${month}`;
};

export const formatMonthYear = (value?: string | Date | null) => {
	const date = toDate(value);
	if (!date) return '—';
	return `${MONTH_NAMES_PT[date.getMonth()]} de ${date.getFullYear()}`;
};

export const formatCycleRange = (start?: string | Date | null, end?: string | Date | null) => {
	if (!start || !end) return '—';
	return `${formatDayMonth(start)} — ${formatDayMonth(end)}`;
};

export const getBillingCopy = (interval?: string, count = 1) => {

	if (interval === 'year') {
		return {
			per: count === 1 ? 'por ano' : `a cada ${count} anos`,
			charge: 'cobrança anual',
		};
	}

	if (interval === 'month') {
		return {
			per: count === 1 ? 'por mês' : `a cada ${count} meses`,
			charge: 'cobrança mensal',
		};
	}

	return { per: '', charge: 'cobrança recorrente' };
};

export const getPaymentMethodLabel = (method?: string | null) =>
	(method && PAYMENT_METHOD_LABEL[method]) || '—';

export const getPaymentMethodSuffix = (method?: string | null) =>
	(method && PAYMENT_METHOD_SUFFIX[method]) || '';

export const formatDocument = (document?: string | null, documentType?: string | null) => {
	if (!document) return '—';
	const digits = document.replace(/\D/g, '');

	if (documentType === 'cnpj' || digits.length === 14) {
		return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
	}

	if (documentType === 'cpf' || digits.length === 11) {
		return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
	}

	return document;
};

export const getDocumentTypeLabel = (documentType?: string | null) => {
	if (documentType === 'cnpj') return 'CNPJ';
	if (documentType === 'cpf') return 'CPF';
	return 'Documento';
};

export const formatPlanPrice = (cents?: number | null) => {
	const value = (cents ?? 0) / 100;
	const hasCents = !Number.isInteger(value);
	return value.toLocaleString('pt-BR', {
		minimumFractionDigits: hasCents ? 2 : 0,
		maximumFractionDigits: 2,
	});
};

export const getAudienceLabel = (role?: string | null, name?: string | null) => {
	const haystack = `${role ?? ''} ${name ?? ''}`.toLowerCase();
	if (haystack.includes('farmer') || haystack.includes('agricultor') || haystack.includes('basic')) {
		return 'PARA AGRICULTORES';
	}
	if (
		haystack.includes('consultant') ||
		haystack.includes('consultor') ||
		haystack.includes('agronom') ||
		haystack.includes('professional')
	) {
		return 'PARA AGRONOMOS E CONSULTORES';
	}
	if (haystack.includes('company') || haystack.includes('empresa') || haystack.includes('business')) {
		return 'PARA EMPRESAS E COOPERATIVAS';
	}
	return 'PARA SUA OPERAÇÃO';
};

export const isFeaturedCatalogPlan = (name?: string | null, role?: string | null) => {
	const haystack = `${name ?? ''} ${role ?? ''}`.toLowerCase();
	return haystack.includes('professional') || haystack.includes('consultant');
};

export const formatChargeDescription = (description?: string | null) => {
	if (!description) return '—';
	return description
		.replace(' - ', ' · ')
		.replace(/\bmensal\b/i, 'mensalidade')
		.replace(/\banual\b/i, 'anuidade');
};

export const getChargeStatusLabel = (status?: string | null) => {
	switch (status) {
		case 'paid':
			return 'Pago';
		case 'canceled':
			return 'Cancelado';
		case 'processing':
			return 'Processando';
		case 'failed':
			return 'Falhou';
		case 'refunded':
			return 'Reembolsado';
		default:
			return status || '—';
	}
};

export const computeYearlyDiscount = (
	monthPrice?: number | null,
	yearPrice?: number | null
) => {
	if (!monthPrice || !yearPrice) return 17;
	const monthlyOfYear = yearPrice / 12;
	const percent = Math.round((1 - monthlyOfYear / monthPrice) * 100);
	if (!Number.isFinite(percent) || percent <= 0 || percent >= 80) return 17;
	return percent;
};

const PLAN_TIER_RANK: Record<string, number> = {
	farmer: 1,
	consultant: 2,
	company: 3,
	master: 4,
	admin: 4,
};

export const getPlanTierRank = (role?: string | null) =>
	(role && PLAN_TIER_RANK[role.toLowerCase()]) || 0;


